// src/lib/roomCommerce.js
//
// Room tickets and collection unlocks paid from the Lunexx balance (the
// room-commerce Edge Function). The server decides everything: what is on
// sale, the price, who may buy. A purchase sends back the price and revision
// it was quoted; if the creator changed the price meanwhile the server
// refuses with `price_changed` (nothing charged) and the caller re-quotes.

import { supabase } from './supabase';

/** call room-commerce; a refusal throws an Error carrying the server body
 *  (code, reason, price_cents, price_revision, min_cents, max_cents, needed) */
import { balanceChanged } from '../hooks/useBalanceSummary';

const SPENDS = new Set(['buy_ticket', 'unlock_collection', 'buy']);

export async function roomCommerce(authToken, body) {
  const { data, error } = await supabase.functions.invoke('room-commerce', {
    body, headers: { 'X-Lunexx-Auth': `Bearer ${authToken}` },
  });
  if (error) {
    let payload = {};
    if (error.context?.json) {
      try { payload = (await error.context.json()) || {}; } catch { /* noop */ }
    }
    const e = new Error(payload.error || error.message || 'Request failed');
    Object.assign(e, payload, { code: payload.code });
    throw e;
  }
  if (SPENDS.has(body?.action)) balanceChanged();
  return data;
}

/** the platform default / min / max for an editor (credits_item_price_bounds) */
export const itemBounds = (authToken, product) =>
  roomCommerce(authToken, { action: 'bounds', product }).then((r) => r?.bounds ?? null);

export const quoteItem = (authToken, product, itemId) =>
  roomCommerce(authToken, { action: 'quote', product, itemId });

export const buyTicket = (authToken, { roomId, expectedCents, priceRevision }) =>
  roomCommerce(authToken, { action: 'buy_ticket', roomId, expectedCents, priceRevision });

export const unlockCollection = (authToken, { collectionId, expectedCents, priceRevision }) =>
  roomCommerce(authToken, { action: 'unlock_collection', collectionId, expectedCents, priceRevision });

export const setItemPrice = (authToken, { product, itemId, priceCents }) =>
  roomCommerce(authToken, { action: 'set_price', product, itemId, priceCents });

/** a credits room's replay, quoted and bought from the balance (the replays
 *  function; mission 004 Stage 2). The buyer sends back what they were quoted. */
export async function replaysCredits(authToken, body) {
  const { data, error } = await supabase.functions.invoke('replays', {
    body, headers: { 'X-Lunexx-Auth': `Bearer ${authToken}` },
  });
  if (error) {
    let payload = {};
    if (error.context?.json) {
      try { payload = (await error.context.json()) || {}; } catch { /* noop */ }
    }
    const e = new Error(payload.error || error.message || 'Request failed');
    Object.assign(e, payload, { code: payload.code });
    throw e;
  }
  if (SPENDS.has(body?.action)) balanceChanged();
  return data;
}
export const quoteReplay = (authToken, roomId) => replaysCredits(authToken, { action: 'credits_quote', roomId });
export const buyReplay = (authToken, { roomId, expectedCents, priceRevision }) =>
  replaysCredits(authToken, { action: 'buy', roomId, expectedCents, priceRevision });

/** errors that send the buyer to Add funds */
export const FUNDS_CODES = new Set(['insufficient_funds']);

/** "$12" for whole dollars, "$12.50" otherwise */
export function formatTicketUsd(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n) || n <= 0) return '';
  return n % 100 === 0 ? `$${n / 100}` : `$${(n / 100).toFixed(2)}`;
}

/** "12.50" (dollars, as typed in an editor) → 1250 cents; '' → null (free); junk → NaN */
export function dollarsToCents(v) {
  const s = String(v ?? '').trim();
  if (s === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return NaN;
  return Math.round(Number(s) * 100);
}
